<template>
  <div class="tw-flex tw-items-end">
    <BaseMultiSelect
      v-model="filters.ownership"
      style="padding-bottom: 0"
      :small="false"
      :options="ownershipFilterOptions"
      :locked-width="true"
    >
      <template #all-selected>
        {{ $t('Ownership status') }}
      </template>
      <template #no-selection>
        {{ $t('Ownership status') }}
      </template>
    </BaseMultiSelect>
    <BaseMultiSelect
      v-model="filters.contractStatus"
      style="padding-bottom: 0"
      :small="false"
      :options="contractStatusOptions"
      :locked-width="true"
    >
      <template #all-selected>
        {{ $t('Contract status') }}
      </template>
      <template #no-selection>
        {{ $t('Contract status') }}
      </template>
    </BaseMultiSelect>
    <div class="m-r-5">
      <label :class="{ 'tw-invisible': !showInputDateRange }" class="tw-text-gray-700 tw-block">
        {{ $t('Contract start date') }}
      </label>
      <BaseButton v-if="!showInputDateRange" class="tw-pt-10" @click="showInputDateRange = true">
        {{ $t('Filter by contract start date') }}
      </BaseButton>
      <InputDateRange v-show="showInputDateRange" v-model:date-range="filters.contractDateRange" />
    </div>
    <BaseMultiSelect
      v-model="filters.companies"
      style="padding-bottom: 0"
      :small="false"
      :options="companies"
      :locked-width="true"
    >
      <template #all-selected>
        {{ $t('Company name') }}
      </template>
      <template #no-selection>
        {{ $t('Company name') }}
      </template>
    </BaseMultiSelect>
    <BaseMultiSelect
      v-model="filters.workers"
      style="padding-bottom: 0"
      :small="false"
      :options="workers"
      :locked-width="true"
    >
      <template #all-selected>
        {{ $t('Worker name') }}
      </template>
      <template #no-selection>
        {{ $t('Worker name') }}
      </template>
    </BaseMultiSelect>
    <WdsButtonGhost secondary @click="resetFilters">
      {{ $t('Reset filters') }}
    </WdsButtonGhost>
  </div>
</template>

<script setup>
import { watch, ref, computed } from 'vue'
import { WdsButtonGhost } from '@worksome/wds-components'
import moment from 'moment'
import gql from 'graphql-tag'
import { useQuery } from '@vue/apollo-composable'
import BaseMultiSelect from '@/components/BaseMultiSelect.vue'
import InputDateRange from '@/components/InputDateRange.vue'
import BaseButton from '@/components/BaseButton.vue'
import DateRange from '@/vue-prop-types/DateRange'

const { result: companyAndWorkers } = useQuery(
  gql`
    query hires {
      hires(first: 100000) {
        data {
          worker {
            name
            id
          }
          company {
            id
            name
          }
        }
      }
    }
  `,
  {},
  {
    clientId: 'publicApi',
  },
)

const emit = defineEmits(['updateFilters'])

const filters = ref({
  ownership: [],
  contractDateRange: new DateRange({ unsafe: true }),
  companies: [],
  workers: [],
  contractStatus: [],
})

const resetFilters = () => {
  filters.value.ownership = []
  filters.value.contractDateRange = new DateRange({ unsafe: true })
  filters.value.companies = []
  filters.value.workers = []
  filters.value.contractStatus = []
}

watch(filters.value, (updatedFilter) => {
  // Map over filter to format data as expected
  const fromDate = updatedFilter.contractDateRange.DateStart
    ? moment(updatedFilter.contractDateRange.DateStart).format('YYYY-MM-DD')
    : null
  const toDate = updatedFilter.contractDateRange.DateEnd
    ? moment(updatedFilter.contractDateRange.DateEnd).format('YYYY-MM-DD')
    : null

  emit('updateFilters', {
    companies: updatedFilter.companies.map(({ value }) => value),
    workers: updatedFilter.workers.map(({ value }) => value),
    recruiterOwnershipIsExpired:
      updatedFilter.ownership.length === 1 ? updatedFilter.ownership[0].value : null,
    startDateRange: fromDate || toDate ? { from: fromDate, to: toDate } : null,
    contractStatus: updatedFilter.contractStatus.map(({ value }) => value),
  })
})

const getDistinctFilterValues = (key) =>
  Object.values(
    companyAndWorkers.value?.hires?.data?.reduce(
      (acc, obj) => ({
        ...acc,
        [obj[key].id]: { name: obj[key].name, value: obj[key].id },
      }),
      {},
    ) ?? {},
  )

// Ownership
const ownershipFilterOptions = [
  { name: $t('Expired'), value: true },
  { name: $t('Active'), value: false },
]

// Contract status
const contractStatusOptions = [
  { name: $t('Draft'), value: 'DRAFT' },
  { name: $t('Active'), value: 'ACTIVE' },
  { name: $t('Archived'), value: 'ARCHIVED' },
]

// Date range
const showInputDateRange = ref(false)

// Companies
const companies = computed(() => getDistinctFilterValues('company'))

// Workers
const workers = computed(() => getDistinctFilterValues('worker'))
</script>
